// Voice accessibility — volume, speed and captions for every spoken reply.
// Preferences persist across reloads; captions are broadcast on a tiny bus so
// any surface (Mrs DNA, chat, lessons) can show what is being said aloud.
import { getOption, setOption } from "./persistOptions";
import { useEffect, useState } from "react";

export interface VoiceA11y {
  volume: number;   // 0..1
  speed: number;    // 0.6..1.6 multiplier applied on top of her natural rate
  captions: boolean;
}

const KEY = "voice:a11y";
export const DEFAULT_VOICE_A11Y: VoiceA11y = { volume: 1, speed: 1, captions: false };

export function getVoiceA11y(): VoiceA11y {
  const raw = getOption<Partial<VoiceA11y>>(KEY, {});
  const merged = { ...DEFAULT_VOICE_A11Y, ...raw };
  return {
    volume: clamp(merged.volume, 0, 1, DEFAULT_VOICE_A11Y.volume),
    speed: clamp(merged.speed, 0.6, 1.6, DEFAULT_VOICE_A11Y.speed),
    captions: !!merged.captions,
  };
}

export function setVoiceA11y(patch: Partial<VoiceA11y>) {
  setOption(KEY, { ...getVoiceA11y(), ...patch });
  listeners.forEach((l) => l(getVoiceA11y()));
}

function clamp(n: number, lo: number, hi: number, fallback = lo) {
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fallback;
}

const listeners = new Set<(v: VoiceA11y) => void>();

export function useVoiceA11y(): [VoiceA11y, (p: Partial<VoiceA11y>) => void] {
  const [v, setV] = useState<VoiceA11y>(() => getVoiceA11y());
  useEffect(() => {
    listeners.add(setV);
    setV(getVoiceA11y());
    return () => { listeners.delete(setV); };
  }, []);
  return [v, setVoiceA11y];
}

/* ------------------------------- captions ------------------------------- */

type CaptionFn = (text: string | null) => void;
const captionListeners = new Set<CaptionFn>();

/** Called by the speech engines whenever a line starts being spoken. */
export function publishCaption(text: string | null) {
  if (!getVoiceA11y().captions) return;
  captionListeners.forEach((l) => l(text));
}

export function subscribeCaptions(fn: CaptionFn) {
  captionListeners.add(fn);
  return () => { captionListeners.delete(fn); };
}

export function useCaption(): string | null {
  const [caption, setCaption] = useState<string | null>(null);
  useEffect(() => subscribeCaptions(setCaption), []);
  return caption;
}

/** Apply the user's accessibility preferences to an utterance. */
export function applyVoiceA11y(u: SpeechSynthesisUtterance, baseRate: number) {
  const a = getVoiceA11y();
  // A stored 0 would make her silent forever; treat it as "muted by mistake".
  u.volume = a.volume > 0 ? a.volume : 1;
  u.rate = clamp(baseRate * a.speed, 0.5, 2, 1);
}
